/**
 * Reading a meeting link well enough to label its button.
 *
 * The server already refuses anything that is not http(s) when a link is saved;
 * this is the second lock on the same door, because a value can arrive from an
 * older client or a synced calendar and an `href` is the one place a
 * `javascript:` URL does damage.
 */

export type MeetingProvider = "teams" | "meet" | "zoom" | "other";

/** The link if it is a plain http(s) URL, otherwise null. */
export function safeMeetingUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

const hostIs = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

export function meetingProvider(value: string): MeetingProvider {
  const safe = safeMeetingUrl(value);
  if (!safe) return "other";
  const host = new URL(safe).hostname.toLowerCase();
  if (hostIs(host, "teams.microsoft.com") || hostIs(host, "teams.live.com")) return "teams";
  if (hostIs(host, "meet.google.com")) return "meet";
  if (hostIs(host, "zoom.us") || hostIs(host, "zoomgov.com")) return "zoom";
  return "other";
}

const LABELS: Record<MeetingProvider, string> = {
  teams: "Join Teams meeting",
  meet: "Join Google Meet",
  zoom: "Join Zoom meeting",
  other: "Join meeting",
};

export const joinLabel = (value: string): string => LABELS[meetingProvider(value)];
